import { create } from 'zustand';
import type { LogicIssue, LogicIssueCategory, LogicResult } from '../types/deviceModel';
import { healthErrors } from '../engine/networkLogic';

export type LogicIssueGroupBy = 'device' | 'issue';

interface LogicUiState {
  drawerOpen: boolean;
  hideIdle: boolean;
  issueFilter: LogicIssueCategory | 'all';
  search: string;
  issueGroupBy: LogicIssueGroupBy;
  /** null = whole design */
  focusDeviceId: string | null;
  result: LogicResult | null;
  selectionIds: string[];
  setDrawerOpen: (open: boolean) => void;
  setHideIdle: (hide: boolean) => void;
  setIssueFilter: (f: LogicIssueCategory | 'all') => void;
  setSearch: (q: string) => void;
  setIssueGroupBy: (v: LogicIssueGroupBy) => void;
  setFocusDeviceId: (id: string | null) => void;
  setResult: (r: LogicResult | null) => void;
  setSelectionIds: (ids: string[]) => void;
  toggleSelectionId: (id: string, additive: boolean) => void;
}

export const useLogicStore = create<LogicUiState>((set) => ({
  drawerOpen: false,
  hideIdle: true,
  issueFilter: 'all',
  search: '',
  issueGroupBy: 'device',
  focusDeviceId: null,
  result: null,
  selectionIds: [],
  setDrawerOpen: (drawerOpen) => set({ drawerOpen }),
  setHideIdle: (hideIdle) => set({ hideIdle }),
  setIssueFilter: (issueFilter) => set({ issueFilter }),
  setSearch: (search) => set({ search }),
  setIssueGroupBy: (issueGroupBy) => set({ issueGroupBy }),
  setFocusDeviceId: (focusDeviceId) => set({ focusDeviceId }),
  setResult: (result) => set({ result }),
  setSelectionIds: (selectionIds) => set({ selectionIds }),
  toggleSelectionId: (id, additive) =>
    set((s) => {
      if (!additive) return { selectionIds: [id] };
      const has = s.selectionIds.includes(id);
      return { selectionIds: has ? s.selectionIds.filter((x) => x !== id) : [...s.selectionIds, id] };
    }),
}));

export function visibleLogicIssues(result: LogicResult | null, hideIdle: boolean, filter: LogicIssueCategory | 'all', search: string): LogicIssue[] {
  if (!result) return [];
  const q = search.trim().toLowerCase();
  return result.issues.filter((i) => {
    if (hideIdle) {
      const dev = i.equipmentId ? result.devices[i.equipmentId] : undefined;
      if (dev?.idle && (i.code === 'UNPOWERED' || i.code === 'MISSING_PSU' || i.severity === 'info')) return false;
      if (i.idle) return false;
    }
    if (filter !== 'all' && i.category !== filter) return false;
    if (q) {
      const blob = `${i.message} ${i.details || ''} ${i.equipmentId || ''} ${i.cableId || ''}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });
}

export function logicHealthOk(result: LogicResult | null, hideIdle: boolean): boolean {
  if (!result) return true;
  return healthErrors(result, hideIdle).length === 0;
}
